/**
 * @file PlanTypeDisplayPropertiesModal.js
 * @description Tabbed Display Properties modal for a ClientPlanType.
 */

import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Toast from '../ui/toasts/Toast';
import Spinner from '../ui/spinners/Spinner';
import PlanTypeGeneralTab from './_tabs/PlanTypeGeneralTab';
import PlanTypeImageTab from './_tabs/PlanTypeImageTab';
import PlanTypeCategoriesItemsTab from './_tabs/PlanTypeCategoriesItemsTab';
import {
    createPlanType,
    fetchPlanType,
    updatePlanType,
} from '../../services/benefitPlanService';
import {
    DISPLAY_MODE,
    displayModeToId,
    resolveDisplayMode,
} from './_helpers/planTypeDisplayMode';
import styles from './PlanTypeDisplayProperties.module.scss';
import { getText } from '../../i18n';

const isLocalBenefitPlanImageUrl = imageUrl =>
    typeof imageUrl === 'string' && imageUrl.startsWith('/benefitPlan/');

const TABS = {
    GENERAL: 'General',
    IMAGE: 'Image',
    CATEGORIES_ITEMS: 'Categories + Items',
};

function buildDraft(viewModel, isAddMode) {
    if (isAddMode) {
        const duplicatedFromPlanTypeId =
            viewModel?.planTypeId ??
            viewModel?.planType?.id ??
            viewModel?.duplicatedFromPlanTypeId ??
            null;
        return {
            planTypeId: null,
            duplicatedFromPlanTypeId,
            nameOverride: '',
            originalName: null,
            codeOverride: '',
            originalCode: null,
            displayMode: duplicatedFromPlanTypeId
                ? resolveDisplayMode(viewModel)
                : DISPLAY_MODE.ENROLLMENT_ONLY,
            populationGroups: duplicatedFromPlanTypeId
                ? (viewModel?.populationGroups ?? [])
                : [],
            imageUrl: duplicatedFromPlanTypeId
                ? (viewModel?.imageUrl ?? null)
                : null,
            imageAssetId: duplicatedFromPlanTypeId
                ? (viewModel?.imageAssetId ?? null)
                : null,
        };
    }

    const displayName = viewModel?.name ?? viewModel?.planType?.name ?? '';
    return {
        nameOverride: displayName,
        originalName: viewModel?.name ?? null,
        codeOverride: viewModel?.code ?? viewModel?.planType?.code ?? '',
        originalCode: viewModel?.code ?? null,
        displayMode: resolveDisplayMode(viewModel),
        populationGroups: viewModel?.populationGroups ?? [],
        imageUrl: viewModel?.imageUrl ?? null,
        imageAssetId: viewModel?.imageAssetId ?? null,
    };
}

const PlanTypeDisplayPropertiesModal = ({
    viewModel,
    planYearId,
    planYearLabel,
    onHide,
    onSaved,
    isAddMode = false,
    zIndex = 1040,
}) => {
    const [activeTab, setActiveTab] = useState(TABS.GENERAL);
    // Hydrated view-model — starts as the list-row shell, then replaced
    // with the fresh `GET /plan-type/{id}` response once it lands.
    const [hydratedViewModel, setHydratedViewModel] = useState(viewModel);
    const [draft, setDraft] = useState(() => buildDraft(viewModel, isAddMode));
    const [isSaving, setIsSaving] = useState(false);
    const [isLoadingDetail, setIsLoadingDetail] = useState(false);
    const [tabDirty, setTabDirty] = useState(false);
    const [isImageUploading, setIsImageUploading] = useState(false);

    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, []);

    // Edit-mode: pull the full record so the modal has populationGroups
    // objects, imageUrl, displayMode, etc. (BACKEND_REQUIREMENTS_ADMIN.md §1b)
    useEffect(() => {
        setHydratedViewModel(viewModel);
        setDraft(buildDraft(viewModel, isAddMode));
        setActiveTab(TABS.GENERAL);

        if (isAddMode || !viewModel?.id) return undefined;

        let cancelled = false;
        setIsLoadingDetail(true);
        fetchPlanType(viewModel.id, planYearId)
            .then(full => {
                if (cancelled || !full) return;
                setHydratedViewModel(full);
                setDraft(buildDraft(full, false));
            })
            .catch(error => {
                if (cancelled) return;
                console.error('fetchPlanType failed', error);
                // Fall through silently — the modal still works against the
                // shell view-model until the backend ships §1b.
            })
            .finally(() => {
                if (!cancelled) setIsLoadingDetail(false);
            });

        return () => {
            cancelled = true;
        };
    }, [viewModel, isAddMode]);

    const planTypeName = useMemo(() => {
        if (isAddMode) return 'Add New';
        const name =
            hydratedViewModel?.name ?? hydratedViewModel?.planType?.name ?? '';
        return planYearLabel ? `${name} ${planYearLabel}` : name;
    }, [hydratedViewModel, isAddMode, planYearLabel]);

    const handleDraftChange = patch => {
        setDraft(prev => ({ ...prev, ...patch }));
    };

    const validate = () => {
        if (!draft.nameOverride || !draft.nameOverride.trim()) {
            Toast.error(getText('msg_d7659e2c4af2'));
            return false;
        }
        if (!draft.codeOverride || !draft.codeOverride.trim()) {
            Toast.error(getText('msg_ea6ac41a6ab6'));
            return false;
        }
        if (
            draft.displayMode === DISPLAY_MODE.POPULATION &&
            (draft.populationGroups?.length ?? 0) === 0
        ) {
            Toast.error(getText('msg_b607b3560cf7'));
            return false;
        }
        // Image is no longer required to save a plan type. Kept code in case of reversal.
        // if (!draft.imageUrl) {
        //     setActiveTab(TABS.IMAGE);
        //     Toast.error(getText('msg_7164ef3ec3cd'));
        //     return false;
        // }
        return true;
    };

    const buildSavePayload = () => {
        const populationGroupIds = (draft.populationGroups || []).map(
            g => g.id,
        );

        const trimmedName = draft.nameOverride?.trim() || null;
        const systemName = hydratedViewModel?.planType?.name ?? null;
        const userChangedName = trimmedName !== systemName;
        const resolvedName =
            draft.originalName || userChangedName ? trimmedName : null;

        const trimmedCode = draft.codeOverride?.trim() || null;
        const systemCode = hydratedViewModel?.planType?.code ?? null;
        const userChangedCode = trimmedCode !== systemCode;
        const resolvedCode =
            draft.originalCode || userChangedCode ? trimmedCode : null;

        const resolvedPlanYearId =
            planYearId ?? hydratedViewModel?.planYearId ?? null;

        const base = {
            planTypeDisplayToId: displayModeToId(draft.displayMode),
            populationGroupIds,
            name: resolvedName,
            code: resolvedCode,
            imageUrl:
                draft.imageAssetId != null ||
                !isLocalBenefitPlanImageUrl(draft.imageUrl)
                    ? (draft.imageUrl ?? null)
                    : null,
            imageAssetId: draft.imageAssetId ?? null,
            planYearId: resolvedPlanYearId,
        };

        if (isAddMode) {
            return {
                ...base,
                planTypeId: draft.planTypeId ?? null,
                duplicatedFromPlanTypeId:
                    draft.duplicatedFromPlanTypeId ?? null,
            };
        }
        return {
            id: hydratedViewModel.id,
            planTypeId: hydratedViewModel.planTypeId ?? null,
            duplicatedFromPlanTypeId:
                hydratedViewModel.duplicatedFromPlanTypeId ?? null,
            ...base,
        };
    };

    const handleSave = async () => {
        if (!validate()) return;
        try {
            setIsSaving(true);
            const payload = buildSavePayload();
            const result = isAddMode
                ? await createPlanType(payload)
                : await updatePlanType(payload);
            Toast.success(
                isAddMode
                    ? 'Plan type created successfully.'
                    : 'Plan type updated successfully.',
            );
            onSaved?.(result);
        } catch (error) {
            console.error('Save plan type failed', error);
            const detail = error?.response?.data?.detail;
            Toast.error(detail || 'Failed to save plan type.');
        } finally {
            setIsSaving(false);
        }
    };

    const visibleTabs = isAddMode
        ? [TABS.GENERAL, TABS.IMAGE]
        : [TABS.GENERAL, TABS.IMAGE, TABS.CATEGORIES_ITEMS];

    return (
        <div
            className={`${styles.modalBackdrop} modal-backdrop`}
            style={{ zIndex }}
        >
            <div
                className="modal fade show display-block"
                tabIndex="-1"
                aria-modal="true"
                role="dialog"
            >
                <div
                    className={`modal-dialog modal-dialog-centered ${styles.modalDialog}`}
                    style={{ width: '100%', maxWidth: '950px' }}
                >
                    <div
                        className={`modal-content ${styles.modalContent}`}
                        style={{
                            width: '100%',
                            height: '700px',
                            maxHeight: '700px',
                        }}
                    >
                        {/* Header */}
                        <div className={styles.header}>
                            <div className={styles.titleBlock}>
                                <h5 className={styles.title}>
                                    {getText('msg_49135a1db051')}
                                </h5>
                                <p className={styles.subtitle}>
                                    {planTypeName}
                                </p>
                            </div>
                            <button
                                type="button"
                                className={styles.closeBtn}
                                onClick={onHide}
                            >
                                <i className="bi bi-x" />{' '}
                                {getText('msg_bbfa773e5a63')}
                            </button>
                        </div>

                        {/* Tabs */}
                        <ul className={styles.tabBar} role="tablist">
                            {visibleTabs.map(tab => (
                                <li
                                    key={tab}
                                    role="tab"
                                    aria-selected={activeTab === tab}
                                    className={`${styles.tabItem} ${
                                        activeTab === tab ? styles.active : ''
                                    }`}
                                    onClick={() => setActiveTab(tab)}
                                >
                                    {tab}
                                </li>
                            ))}
                        </ul>

                        {/* Tab panel */}
                        <div className={styles.tabPanel}>
                            {isLoadingDetail && (
                                <div className={styles.detailLoadingOverlay}>
                                    <Spinner width={36} />
                                </div>
                            )}
                            {activeTab === TABS.GENERAL && (
                                <PlanTypeGeneralTab
                                    viewModel={hydratedViewModel}
                                    draft={draft}
                                    onChange={handleDraftChange}
                                    isAddMode={isAddMode}
                                />
                            )}
                            {activeTab === TABS.IMAGE && (
                                <PlanTypeImageTab
                                    viewModel={hydratedViewModel}
                                    draft={draft}
                                    onChange={handleDraftChange}
                                    onUploadingChange={setIsImageUploading}
                                />
                            )}
                            {activeTab === TABS.CATEGORIES_ITEMS &&
                                !isAddMode && (
                                    <PlanTypeCategoriesItemsTab
                                        planTypeId={hydratedViewModel?.id}
                                        planYearId={planYearId}
                                        onDirtyChange={setTabDirty}
                                    />
                                )}
                        </div>

                        {/* Footer — Categories+Items has its own Save inside the tab; Image tab hides it while uploading */}
                        {activeTab !== TABS.CATEGORIES_ITEMS &&
                            !(activeTab === TABS.IMAGE && isImageUploading) && (
                                <div className={styles.footer}>
                                    <span className={styles.requiredHint}>
                                        <span className="text-danger">*</span>
                                        {getText('msg_7c118f042eaa')}
                                    </span>
                                    <div className={styles.footerActions}>
                                        <button
                                            type="button"
                                            className={`button-base ing-button-primary-left ${styles.saveButton}`}
                                            onClick={handleSave}
                                            disabled={isSaving}
                                        >
                                            {isSaving ? 'Saving…' : 'Save'}
                                        </button>
                                    </div>
                                </div>
                            )}
                    </div>
                </div>
            </div>
        </div>
    );
};

PlanTypeDisplayPropertiesModal.propTypes = {
    viewModel: PropTypes.object,
    planYearId: PropTypes.number,
    planYearLabel: PropTypes.string,
    onHide: PropTypes.func.isRequired,
    onSaved: PropTypes.func,
    isAddMode: PropTypes.bool,
    zIndex: PropTypes.number,
};

export default PlanTypeDisplayPropertiesModal;
